import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/20/solid';

function MultiSelectDropdown<T extends string | number>({
  label,
  options,
  selected,
  onChange,
  renderOption,
  enableSearch,
  onSearch,
  onScrollToEnd,
  loading,
  summary,
}: {
  label: string;
  options: T[];
  selected: Set<T>;
  onChange: (newSelected: Set<T>) => void;
  renderOption?: (value: T) => string;
  enableSearch?: boolean;
  onSearch?: (search: string) => void;
  onScrollToEnd?: () => void;
  loading?: boolean;
  summary: string;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  const toggleOption = (value: T) => {
    const newSelected = new Set(selected);
    newSelected.has(value) ? newSelected.delete(value) : newSelected.add(value);
    onChange(newSelected);
  };

  const toggleAll = () => {
    onChange(selected.size === options.length ? new Set() : new Set(options));
  };

  const selectedCount = options.filter(opt => selected.has(opt)).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Filter options if search is enabled and search is not empty
  const filteredOptions = enableSearch && search
    ? options.filter(opt => (renderOption ? renderOption(opt) : String(opt)).toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="relative inline-block text-left w-52" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 flex justify-between items-center"
      >
        <span>{summary}</span>
        {open ? (
          <ChevronUpIcon className="h-3 w-3 text-gray-500" />
        ) : (
          <ChevronDownIcon className="h-3 w-3 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
          {enableSearch && (
            <div className="p-1.5">
              <input
                type="text"
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  onSearch?.(e.target.value);
                }}
              />
            </div>
          )}
          <ul
            className="py-1 text-xs text-gray-700"
            onScroll={e => {
              const target = e.target as HTMLUListElement;
              if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
                onScrollToEnd?.();
              }
            }}
          >
            <li
              className="px-3 py-1.5 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
              onClick={toggleAll}
            >
              <span>Select All {label}</span>
              {selectedCount === options.length && options.length > 0 && (
                <CheckIcon className="h-3 w-3 text-blue-700" />
              )}
            </li>
            <hr />
            {filteredOptions.map((opt) => (
              <li
                key={opt}
                className="px-3 py-1.5 flex justify-between items-center hover:bg-gray-100 cursor-pointer"
                onClick={() => toggleOption(opt)}
              >
                <span>{renderOption ? renderOption(opt) : opt}</span>
                {selected.has(opt) && (
                  <CheckIcon className="h-3 w-3 text-blue-700" />
                )}
              </li>
            ))}
            {loading && (
              <li className="px-3 py-1.5 text-center text-gray-400">Loading...</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

type FilterPanelProps = {
  selectedCreationYears: Set<number>;
  selectedArrivalYears: Set<number>;
  selectedManagers: Set<string>;
  selectedSegments: Set<string>;
  selectedClients: Set<string>;
  selectedHotels: Set<string>;
  selectedHotelGroups: Set<string>;
  availableCreationYears: number[];
  availableArrivalYears: number[];
  availableManagers: string[];
  availableSegments: string[];
  clients: string[];
  hotels: string[];
  hotelGroups: string[];
  setSelectedCreationYears: (years: Set<number>) => void;
  setSelectedArrivalYears: (years: Set<number>) => void;
  setSelectedManagers: (managers: Set<string>) => void;
  setSelectedSegments: (segments: Set<string>) => void;
  resetFilters: () => void;
  selectedArrivalMonths: Set<number>;
  selectedArrivalQuarters: Set<number>;
  setSelectedArrivalMonths: (months: Set<number>) => void;
  setSelectedArrivalQuarters: (quarters: Set<number>) => void;
  selectedCreationMonths: Set<number>;
  selectedCreationQuarters: Set<number>;
  setSelectedCreationMonths: (months: Set<number>) => void;
  setSelectedCreationQuarters: (quarters: Set<number>) => void;
  onClientsChange: (newSelected: Set<string>) => void;
  allClientsSelected: boolean;
  onHotelsChange: (newSelected: Set<string>) => void;
  allHotelsSelected: boolean;
  onHotelGroupsChange: (newSelected: Set<string>) => void;
  allHotelGroupsSelected: boolean;
  // NEW: Apply/Reset props
  onApplyFilters: () => void;
  hasUnappliedChanges: boolean;
  // NEW: Market filter (optional)
  selectedMarkets?: Set<string>;
  markets?: string[];
  allMarketsSelected?: boolean;
  onMarketsChange?: (newSelected: Set<string>) => void;
};

export function FilterPanel({
  selectedCreationYears,
  selectedArrivalYears,
  selectedManagers,
  selectedSegments,
  selectedClients,
  selectedHotels,
  selectedHotelGroups,
  availableCreationYears,
  availableArrivalYears,
  availableManagers,
  availableSegments,
  clients,
  hotels,
  hotelGroups,
  setSelectedCreationYears,
  setSelectedArrivalYears,
  setSelectedManagers,
  setSelectedSegments,
  resetFilters,
  selectedArrivalMonths,
  selectedArrivalQuarters,
  setSelectedArrivalMonths,
  setSelectedArrivalQuarters,
  selectedCreationMonths,
  selectedCreationQuarters,
  setSelectedCreationMonths,
  setSelectedCreationQuarters,
  onClientsChange,
  allClientsSelected,
  onHotelsChange,
  allHotelsSelected,
  onHotelGroupsChange,
  allHotelGroupsSelected,
  onApplyFilters,
  hasUnappliedChanges,
  selectedMarkets,
  markets,
  allMarketsSelected,
  onMarketsChange,
}: FilterPanelProps) {
  // Helper for summary label (generic)
  function getSummary<T>(label: string, selected: Set<T>, options: T[]) {
    const selectedCount = options.filter(opt => selected.has(opt)).length;
    if (selectedCount === options.length && options.length > 0) {
      return `All ${label}`;
    }
    return `${selectedCount} ${label}`;
  }

  return (
    <div className="mb-6 bg-gray-50 rounded-xl shadow-sm border">
      {/* Date-related filters row */}
      <div className="p-4 border-b">
        <div className="flex flex-wrap gap-4 items-end">
          <MultiSelectDropdown
            label="Creation Years"
            options={availableCreationYears}
            selected={selectedCreationYears}
            onChange={setSelectedCreationYears}
            summary={getSummary('Creation Years', selectedCreationYears, availableCreationYears)}
          />
          <MultiSelectDropdown
            label="Creation Months"
            options={Array.from({ length: 12 }, (_, i) => i + 1)}
            selected={selectedCreationMonths}
            onChange={setSelectedCreationMonths}
            summary={getSummary('Creation Months', selectedCreationMonths, Array.from({ length: 12 }, (_, i) => i + 1))}
          />
          <MultiSelectDropdown
            label="Creation Quarters"
            options={[1, 2, 3, 4]}
            selected={selectedCreationQuarters}
            onChange={setSelectedCreationQuarters}
            summary={getSummary('Creation Quarters', selectedCreationQuarters, [1, 2, 3, 4])}
          />
          <MultiSelectDropdown
            label="Arrival Years"
            options={availableArrivalYears}
            selected={selectedArrivalYears}
            onChange={setSelectedArrivalYears}
            summary={getSummary('Arrival Years', selectedArrivalYears, availableArrivalYears)}
          />
          <MultiSelectDropdown
            label="Arrival Months"
            options={Array.from({ length: 12 }, (_, i) => i + 1)}
            selected={selectedArrivalMonths}
            onChange={setSelectedArrivalMonths}
            summary={getSummary('Arrival Months', selectedArrivalMonths, Array.from({ length: 12 }, (_, i) => i + 1))}
          />
          <MultiSelectDropdown
            label="Arrival Quarters"
            options={[1, 2, 3, 4]}
            selected={selectedArrivalQuarters}
            onChange={setSelectedArrivalQuarters}
            summary={getSummary('Arrival Quarters', selectedArrivalQuarters, [1, 2, 3, 4])}
          />
        </div>
      </div>

      {/* Other filters row */}
      <div className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <MultiSelectDropdown
            label="Managers"
            options={availableManagers}
            selected={selectedManagers}
            onChange={setSelectedManagers}
            summary={getSummary('Managers', selectedManagers, availableManagers)}
          />
          <MultiSelectDropdown
            label="Segments"
            options={availableSegments}
            selected={selectedSegments}
            onChange={setSelectedSegments}
            summary={getSummary('Segments', selectedSegments, availableSegments)}
          />
          <MultiSelectDropdown
            label="Clients"
            options={clients}
            selected={selectedClients}
            onChange={onClientsChange}
            enableSearch={true}
            renderOption={opt => opt}
            summary={allClientsSelected ? 'All Clients' : getSummary('Clients', selectedClients, clients)}
          />
          <MultiSelectDropdown
            label="Hotel Groups"
            options={hotelGroups}
            selected={selectedHotelGroups}
            onChange={onHotelGroupsChange}
            enableSearch={true}
            renderOption={opt => opt}
            summary={allHotelGroupsSelected ? 'All Hotel Groups' : getSummary('Hotel Groups', selectedHotelGroups, hotelGroups)}
          />
          <MultiSelectDropdown
            label="Hotels"
            options={hotels}
            selected={selectedHotels}
            onChange={onHotelsChange}
            enableSearch={true}
            renderOption={opt => opt}
            summary={allHotelsSelected ? 'All Hotels' : getSummary('Hotels', selectedHotels, hotels)}
          />
          {selectedMarkets !== undefined && markets !== undefined && onMarketsChange && (
            <MultiSelectDropdown
              label="Markets"
              options={markets}
              selected={selectedMarkets}
              onChange={onMarketsChange}
              enableSearch={true}
              renderOption={opt => opt}
              summary={allMarketsSelected ? 'All Markets' : getSummary('Markets', selectedMarkets, markets)}
            />
          )}
        </div>
        <div className="flex gap-2 mt-4 justify-end pr-4">
          <button
            type="button"
            onClick={onApplyFilters}
            disabled={!hasUnappliedChanges}
            className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
              hasUnappliedChanges
                ? 'bg-action text-white hover:bg-action-hover shadow-sm'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="px-3 py-1 text-xs border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
 